import { DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { PdfService } from '@lib/ui';
import { ConfigurableDialog, DialogModule, DialogService } from '@proget-shared/dialog';
import { TableModule } from '@proget-shared/grid/table';
import { TranslateModule, TranslateService } from '@proget-shared/translate';
import { FileDownloadService } from '@proget-shared/ui/file-download';
import { ProgressBarModule } from '@proget-shared/ui/progress-bar';

@Component({
  selector: 'lib-generated-pin',
  templateUrl: './generated-pin.component.html',
  providers: [DatePipe, PdfService],
  imports: [
    TableModule,
    TranslateModule,
    ProgressBarModule,
    DialogModule,
  ],
})
export class GeneratedPinComponent implements ConfigurableDialog {
  protected readonly columns: { header: string; field: string }[];

  protected items: { pin: string; username: string }[] = [];
  protected qrProgress = 0;
  protected qrGenerating = false;

  private readonly datePipe = inject(DatePipe);
  private readonly dialogService = inject(DialogService);
  private readonly fileDownload = inject(FileDownloadService);
  private readonly pdfService = inject(PdfService);

  constructor() {
    const translateService = inject(TranslateService);

    this.columns = [
      {
        header: translateService.instant('user.generated_pin_list.username.header'),
        field: 'username',
      },
      { header: translateService.instant('user.generated_pin_list.pin.header'), field: 'pin' },
    ];
  }

  onConfiguration(configuration: { items: { pin: string; username: string }[] }): void {
    this.items = configuration.items;
  }

  protected close(): void {
    this.dialogService.dismiss(this);
  }

  protected exportQRCodes(): void {
    this.dialogService.lock(this);
    const rows = [];

    for (let i = 0; i < this.items.length; i += 2) {
      const row = [
        this.items[i],
      ];

      i + 1 < this.items.length ? row.push(this.items[i + 1]) : row.push(null);

      rows.push(row);
    }

    this.pdfService.generateAndDownloadPdf({
      pageSize: 'A4',
      pageMargins: [30, 30, 30, 30],
      content: [
        {
          table: {
            dontBreakRows: true,
            widths: ['*', '*'],
            body: [
              ...rows.map((row) => [
                {
                  columns: [
                    { qr: JSON.stringify(row[0]), version: 4, fit: 100, width: '50%' },
                    {
                      stack: [
                        { text: row[0].username, fontSize: row[0].username.length > 32 ? 11 : 18, alignment: 'left' },
                        { text: `PIN: ${row[0].pin}`, fontSize: row[0].username.length > 32 ? 11 : 18, alignment: 'left' },
                      ],
                      width: '50%',
                    },
                  ],
                  margin: [10, 10, 10, 10],
                },
                row[1]
                  ? {
                      columns: [
                        { qr: JSON.stringify(row[1]), version: 4, fit: 100, width: '50%' },
                        {
                          stack: [
                            { text: row[1].username, fontSize: row[1].username.length > 32 ? 11 : 18, alignment: 'left' },
                            { text: `PIN: ${row[1].pin}`, fontSize: row[1].username.length > 32 ? 11 : 18, alignment: 'left' },
                          ],
                          width: '50%',
                        },
                      ],
                      margin: [10, 10, 10, 10],
                    }
                  : { text: '' },
              ]),
            ],
          },
        },
      ],
    }, `qr_${this.datePipe.transform(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.pdf`).finally(() => {
      this.dialogService.unlock(this);
    });
  }

  protected exportCsv(): void {
    if (!Array.isArray(this.items) || this.items.length === 0) {
      return;
    }

    this.fileDownload.save(
      new Blob(
        [
          this.items.map(
            (data) => [data.username, data.pin].join(',')).join('\r\n'),
        ],
        { type: 'text/csv;charset=utf-8;' }
      ),
      'users-pin.csv'
    );
  }
}
